'use client'

import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import Image from 'next/image'
import { EyeIcon, FileIcon, PackageIcon, ToolboxIcon } from 'lucide-react'
import { formatPriceGross, formatPriceNet, formatNumber, getLocalizedValue, getResizedPictureUrl } from '@/lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { CategoryPath } from '@/components/partials'
import { ProductType } from '@/types/product'
import { useMetaStore } from '@/store'

type PropsType = {
  products: ProductType[]
  title: string
  description?: string
  emptyMessage: string
}

const ProductKitsCard = ({ products, title, description, emptyMessage }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const locale = useLocale()
  const { metaState } = useMetaStore()
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale

  if (!products || products.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageIcon className="h-5 w-5" />
            {title}
          </CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center">
            <p className="text-muted-foreground">{emptyMessage}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ToolboxIcon className="h-5 w-5" />
          {title}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24 text-center">{t('picture')}</TableHead>
                <TableHead className="w-28">{t('code')}</TableHead>
                <TableHead>{t('title')}</TableHead>
                <TableHead>{t('category')}</TableHead>
                <TableHead className="text-right">{t('price_without_tax')}</TableHead>
                <TableHead className="text-right">{t('price_with_tax')}</TableHead>
                <TableHead className="text-right">{t('discount')}</TableHead>
                <TableHead className="text-right">{t('stock')}</TableHead>
                <TableHead className="text-center">{t('status')}</TableHead>
                <TableHead className="w-24 text-center">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map(product => (
                <TableRow key={product.id}>
                  <TableCell className="py-2">
                    <div className="flex items-center justify-center">
                      {product.mainPicture ? (
                        <Image
                          src={getResizedPictureUrl(product.mainPicture.path, 60, 60)}
                          alt={product.mainPicture.name || ''}
                          width={60}
                          height={60}
                          className="border object-contain"
                        />
                      ) : (
                        <div className="flex h-15 w-15 items-center justify-center">
                          <FileIcon className="text-muted-foreground h-8 w-8" />
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    <Badge variant="outline">{product.code}</Badge>
                  </TableCell>
                  <TableCell className="font-medium">
                    {getLocalizedValue(product, 'title', locale, defaultLocale)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono text-xs">
                      <CategoryPath category={product.category} />
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatPriceNet(product.price, locale)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatPriceGross(product.price, locale)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {(product.discountRate ?? 0) > 0 ? `${product.discountRate}%` : '-'}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatNumber(product.stock, locale)}</TableCell>
                  <TableCell className="text-center">
                    {product.active ? (
                      <Badge variant="default" className="bg-green-500">
                        {t('active')}
                      </Badge>
                    ) : (
                      <Badge variant="destructive">{t('inactive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => router.push(`/products/${product.id}`)}
                      title={t('view_product')}>
                      <EyeIcon className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

export default ProductKitsCard
